import React, { useEffect, useState } from "react";
import { formatISTDateTime } from "../utils/dateTime";

function RecentBillsModal({ show, onClose, clinic }) {
    const [bills, setBills] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const [searchReceipt, setSearchReceipt] = useState("");
    const [recentDaysFilter, setRecentDaysFilter] = useState("7");
    const [searchLoading, setSearchLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    useEffect(() => {
        if (show) fetchRecentBills(recentDaysFilter);
    }, [show, recentDaysFilter]);

    const filteredBills = bills.filter((bill) =>
        bill.receiptNo?.toLowerCase().includes(searchReceipt.toLowerCase())
    );

    const searchBillFromDB = async () => {
        if (!searchReceipt.trim()) {
            fetchRecentBills(recentDaysFilter);
            return;
        }

        try {
            setSearchLoading(true);
            setMessage("");
            setMessageType("");

            const token = localStorage.getItem("token");

            const res = await fetch(
                `/v1/bills/search?receiptNo=${searchReceipt}`,
                {
                    headers: {
                        Authorization: "Bearer " + token
                    }
                }
            );

            if (!res.ok) {
                throw new Error("Bill not found");
            }

            const data = await res.json();

            // if single object returned
            setBills(Array.isArray(data) ? data : [data]);

        } catch (err) {
            console.error(err);
            setMessage("Bill not found");
            setMessageType("danger");
            setBills([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const deleteBillByReceiptNo = async (receiptNo) => {
        if (!receiptNo) return;

        const confirmed = window.confirm(
            `Delete bill ${receiptNo}? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            setLoading(true);
            setMessage("");
            setMessageType("");

            const token = localStorage.getItem("token");
            const res = await fetch(
                `/v1/bills/by-receipt-no?receiptNo=${encodeURIComponent(receiptNo)}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: "Bearer " + token
                    }
                }
            );

            if (!res.ok) {
                throw new Error("Failed to delete bill");
            }

            setMessage(`Bill ${receiptNo} deleted successfully`);
            setMessageType("success");
            setSelectedBill((current) =>
                current?.receiptNo === receiptNo ? null : current
            );
            await fetchRecentBills(recentDaysFilter);
        } catch (err) {
            console.error(err);
            setMessage("Failed to delete bill");
            setMessageType("danger");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        setSearchReceipt("");
        setRecentDaysFilter("7");
        setSelectedBill(null);
        setMessage("");
        setMessageType("");
        await fetchRecentBills("7");
    };

    const fetchRecentBills = async (days = recentDaysFilter) => {
        try {
            setLoading(true);
            setMessage("");
            setMessageType("");

            const token = localStorage.getItem("token");

            const res = await fetch(
                `/v1/bills/recent?days=${encodeURIComponent(days || "7")}`,
                {
                    headers: {
                        Authorization: "Bearer " + token
                    }
                }
            );

            if (!res.ok) {
                throw new Error("Failed to fetch recent bills");
            }

            const data = await res.json();

            setBills(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Recent bills error:", err);
            setBills([]);
            setMessage("Failed to load recent bills");
            setMessageType("danger");
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    const calculateGST = (inclusiveAmount, gstRate) => {
        const amount = Number(inclusiveAmount) || 0;
        const rate = Number(gstRate) || 0;

        if (rate <= 0) {
            return {
                basePrice: amount,
                gstAmount: 0,
                cgst: 0,
                sgst: 0
            };
        }

        const base = amount / (1 + rate / 100);
        const gst = amount - base;

        return {
            basePrice: base,
            gstAmount: gst,
            cgst: gst / 2,
            sgst: gst / 2
        };
    };

    const buildPrintMarkup = () => {
        if (!selectedBill) return "";

        const createdAt = selectedBill.createdAt
            ? formatISTDateTime(selectedBill.createdAt)
            : "";

        return `
            <div id="print-section">
                ${clinic?.logoUrl ? `
                    <div style="text-align:center; margin-bottom:8px;">
                        <img
                            src="${clinic.logoUrl}"
                            alt="Clinic Logo"
                            style="max-width:120px; max-height:80px; object-fit:contain;"
                        />
                    </div>
                ` : ""}

                <div class="text-center mb-2">
                    <h5 style="margin:0;">${clinic?.name || ""}</h5>
                    ${clinic?.gstn ? `<small>GSTIN: ${clinic.gstn}</small>` : ""}
                </div>

                <div class="text-center mb-2">
                    <small>
                        ${clinic?.address1 ? `${clinic.address1}<br />` : ""}
                        ${clinic?.address2 ? `${clinic.address2}<br />` : ""}
                        ${clinic?.city && clinic?.state ? `${clinic.city}, ${clinic.state}<br />` : ""}
                        ${clinic?.pin ? `PIN: ${clinic.pin}<br />` : ""}
                    </small>
                </div>

                <hr style="border-top:1px dotted black;" />

                <div class="text-center mb-2">
                    <small>TAX INVOICE</small>
                    <div class="text-center mb-1">
                        <small>Receipt No: ${selectedBill.receiptNo || ""}</small>
                    </div>
                    ${createdAt ? `<div><small>Date: ${createdAt}</small></div>` : ""}
                </div>

                <hr style="border-top:1px dotted black;" />

                <table style="width:100%; font-size:11px; table-layout:fixed;">
                    <thead>
                        <tr>
                            <th style="width:5%;">#</th>
                            <th style="width:44%;">Item</th>
                            <th style="width:10%;">Qty</th>
                            <th style="width:14%;">MRP</th>
                            <th style="width:10%;">Dis</th>
                            <th style="width:17%; text-align:right;">Amt</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${(selectedBill.items || []).map((item, i) => `
                            <tr>
                                <td>${i + 1}</td>
                                <td style="word-break:break-word;">${item.productName || ""}</td>
                                <td>${item.quantity ?? ""}</td>
                                <td>${Number(item.price || 0).toFixed(2)}</td>
                                <td>${item.discountPercent || 0}%</td>
                                <td style="text-align:right;">${Number(item.total || 0).toFixed(2)}</td>
                            </tr>
                        `).join("")}
                    </tbody>
                </table>

                <hr />

                <div style="font-family: monospace; font-size:11px;">
                    <div style="display:flex; justify-content:space-between;">
                        <strong>Total</strong>
                        <strong>${Number(selectedBill.totalAmount || 0).toFixed(2)}</strong>
                    </div>
                </div>
            </div>
        `;
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show d-block">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Recent Bills</h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>

                        <div className="modal-body">
                            {(loading || searchLoading) && (
                                <div className="modal-loading-overlay">
                                    <div className="text-center">
                                        <div className="spinner-border" role="status"></div>
                                        <p className="mt-2 mb-0">Loading...</p>
                                    </div>
                                </div>
                            )}

                            {message && (
                                <div className={`alert alert-${messageType || "info"} py-2`}>
                                    {message}
                                </div>
                            )}

                            {!selectedBill && (
                                <>
                                    <div className="mb-3">
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-5">
                                                <label className="form-label mb-1">Search Receipt</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by Receipt Number"
                                                    value={searchReceipt}
                                                    onChange={(e) => setSearchReceipt(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label mb-1">Last Bills</label>
                                                <select
                                                    className="form-control"
                                                    value={recentDaysFilter}
                                                    onChange={(e) => setRecentDaysFilter(e.target.value)}
                                                >
                                                    <option value="3">Last 3 days</option>
                                                    <option value="7">Last 7 days</option>
                                                    <option value="14">Last 14 days</option>
                                                    <option value="30">Last 30 days</option>
                                                </select>
                                            </div>

                                            <div className="col-md-4 d-flex gap-2">
                                                <button
                                                    className="btn btn-primary flex-grow-1"
                                                    onClick={searchBillFromDB}
                                                    disabled={searchLoading}
                                                >
                                                    {searchLoading ? "Searching..." : "Search"}
                                                </button>

                                                <button
                                                    className="btn btn-secondary flex-grow-1"
                                                    onClick={handleReset}
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="recent-bills-list-scroll border rounded"
                                        style={{ maxHeight: "48vh", overflowY: "auto" }}
                                    >
                                        <table className="table table-sm mb-0">
                                            <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                                                <tr>
                                                    <th>Receipt</th>
                                                    <th>Date</th>
                                                    <th>Total</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {filteredBills.length > 0 ? (
                                                    filteredBills.map((b, i) => (
                                                        <tr key={i}>
                                                            <td>{b.receiptNo}</td>

                                                            <td>
                                                                {formatISTDateTime(b.createdAt)}
                                                            </td>

                                                            <td>₹{b.totalAmount}</td>

                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => setSelectedBill(b)}
                                                                >
                                                                    View
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-danger ms-2"
                                                                    onClick={() => deleteBillByReceiptNo(b.receiptNo)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-4">
                                                            No bills found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* 🧾 PRINT VIEW */}
                            {selectedBill && (
                                <div>

                                    <button
                                        className="btn btn-secondary mb-2"
                                        onClick={() => setSelectedBill(null)}
                                    >
                                        ← Back
                                    </button>

                                    <div id="recent-print">

                                        <div className="text-center mb-2">
                                            <h5>{clinic?.name}</h5>
                                            <small>Receipt: {selectedBill.receiptNo}</small>
                                        </div>

                                        <hr />

                                        <table style={{ width: "100%", fontSize: "12px" }}>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Item</th>
                                                    <th>Qty</th>
                                                    <th>MRP</th>
                                                    <th>Dis</th>
                                                    <th>Amt</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedBill.items?.map((item, i) => (
                                                    <tr key={i}>
                                                        <td>{i + 1}</td>
                                                        <td>{item.productName}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{item.price}</td>
                                                        <td>{item.discountPercent || 0}%</td>
                                                        <td>{item.total}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <hr />

                                        <div className="d-flex justify-content-between">
                                            <strong>Total</strong>
                                            <strong>₹{selectedBill.totalAmount}</strong>
                                        </div>

                                    </div>

                                </div>
                            )}

                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>

                            {selectedBill && (
                                <button
                                    className="btn btn-success"
                                    onClick={() => {
                                        const printContent = buildPrintMarkup();
                                        const win = window.open("", "_blank");

                                        win.document.write(`
                                            <html>
                                            <head>
                                                <title>Print</title>
                                                <style>
                                                    @page {
                                                        size: 80mm auto;
                                                        margin: 0;
                                                    }

                                                    body {
                                                        width: 80mm;
                                                        margin: 0;
                                                        padding: 0;
                                                        font-family: monospace;
                                                        font-size: 12px;
                                                    }

                                                    #print-section {
                                                        width: 68mm;
                                                        margin: 0 auto;
                                                        padding: 2mm;
                                                        font-size: 11px;
                                                    }

                                                    #print-section div {
                                                        max-width: 100%;
                                                        overflow: hidden;
                                                    }

                                                    table {
                                                        width: 100%;
                                                        table-layout: fixed;
                                                    }

                                                    th, td {
                                                        font-size: 11px;
                                                        padding: 1px;
                                                        word-break: break-word;
                                                    }

                                                    img {
                                                        max-width: 100%;
                                                        display: block;
                                                        margin: 0 auto;
                                                    }

                                                    hr {
                                                        border: 0;
                                                        border-top: 1px dotted #000;
                                                    }
                                                </style>
                                            </head>
                                            <body>${printContent}</body>
                                            </html>
                                        `);

                                        win.document.close();
                                        win.onload = () => {
                                            setTimeout(() => win.print(), 400);
                                        };
                                    }}
                                >
                                    Print
                                </button>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

export default RecentBillsModal;


